import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  Badge,
  Spinner,
  useToast,
  Flex,
  Divider,
} from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import Auth from "../../utils/auth";
import {
  getAllcontactMessages,
  readcontacts,

} from "../../utils/api";

const AdminMessages = () => {
  const token = Auth.checkForToken();
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔹 Fetch all messages
  useEffect(() => {
    if (!token) {
      toast({
        title: "Authorization failed",
        description: "Unable to fetch contact messages.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const fetchMessages = async () => {
      try {
        const data = await getAllcontactMessages(token);
        setMessages(data.messages);
      } catch {
        toast({
          title: "Failed to load messages",
          description: "Unable to fetch contact messages.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [toast, token]);

  // 🔹 Mark message as read (UI only, bulk API handles persistence)
  const handleRead = (id) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === id ? { ...msg, is_read: true } : msg
      )
    );
  };

  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // 🔹 Bulk mark unread messages as read on page unmount
  useEffect(() => {
    return () => {
      if (!token) return;

      const currentMsgs = messagesRef.current || [];
      const unreadIds = currentMsgs
        .filter((msg) => !msg.is_read)
        .map((msg) => msg.id);

      if (unreadIds.length > 0) {
        readcontacts(unreadIds, token).catch(() => {
          // silent fail
        });
      }
    };
  }, [token]);

  return (
    <Container maxW="container.lg" py={8}>
      <Heading size="lg" mb={6}>
        Contact Messages
      </Heading>

      {loading ? (
        <Flex justify="center" mt={20}>
          <Spinner size="xl" />
        </Flex>
      ) : messages.length === 0 ? (
        <Text color="gray.500">No messages found.</Text>
      ) : (
        <Stack spacing={4}>
          {messages.map((msg) => (
            <Box
              key={msg.id}
              p={4}
              bg="white"
              borderRadius="md"
              boxShadow="sm"
              overflow="hidden"
              cursor="pointer"
              opacity={msg.is_read ? 0.6 : 1}
              filter={msg.is_read ? "blur(0.6px)" : "none"}
              transition="all 0.2s ease"
              onClick={() => !msg.is_read && handleRead(msg.id)}
            >
              <Flex
                justify="space-between"
                align="center"
                wrap="wrap"
                gap={2}
              >
                <Flex gap={2} align="center">
                  <Badge
                    colorScheme={
                      msg.contact_for === "developer" ? "blue" : "green"
                    }
                  >
                    {msg.contact_for}
                  </Badge>

                  {!msg.is_read && (
                    <Badge colorScheme="red">NEW</Badge>
                  )}
                </Flex>

                <Text fontSize="sm" color="gray.500">
                  {new Date(msg.createdAt).toLocaleString()}
                </Text>
              </Flex>

              <Divider my={3} />

              <Text color="gray.700" whiteSpace="pre-wrap">
                {msg.message}
              </Text>

              <Text mt={3} fontSize="sm" color="gray.500">
                User: {msg.user?.profile?.first_name}{" "}
                {msg.user?.profile?.last_name}
              </Text>
            </Box>
          ))}
        </Stack>
      )}
    </Container>
  );
};

export default AdminMessages;
